
import React from 'react';
import { Filter, X, ArrowDownUp, DollarSign, Calendar, Sparkles } from 'lucide-react';

export interface FilterState {
  minPrice: string;
  maxPrice: string;
  year: string;
  rarity: string;
  sort: 'price-desc' | 'price-asc' | 'date-desc' | 'date-asc';
}

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onApply: () => void;
  mode: 'explore' | 'vault';
}

const RARITIES = [
  "Common", "Uncommon", "Rare", "Double Rare", "Ultra Rare",
  "Illustration Rare", "Special Illustration Rare", "Hyper Rare",
  "Secret Rare", "Promo"
];

const FilterBar: React.FC<Props> = ({ isOpen, onToggle, filters, onChange, onApply, mode }) => {

  const updateFilter = (key: keyof FilterState, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 transition-all text-xs font-black uppercase tracking-widest"
      >
        <Filter className="w-4 h-4" />
        Filters
        {(filters.minPrice || filters.maxPrice || filters.year || filters.rarity) && (
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
        )}
      </button>
    );
  }

  return (
    <div className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-4 mb-6 shadow-xl animate-in slide-in-from-top-2">
      <div className="flex justify-between items-center mb-4 border-b border-slate-800 pb-2">
        <h3 className="text-xs font-black text-white uppercase tracking-widest flex items-center gap-2">
          <Filter className="w-4 h-4 text-blue-500" /> Advanced Filters
        </h3>
        <button type="button" onClick={onToggle} className="text-slate-500 hover:text-white" title="Close filters"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">

        {/* Price Range */}
        <div className="space-y-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <DollarSign className="w-3 h-3" /> Price Range
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.minPrice}
              onChange={(e) => updateFilter('minPrice', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500 outline-none"
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={(e) => updateFilter('maxPrice', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Year */}
        <div className="space-y-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Release Year
          </label>
          <input
            type="number"
            placeholder="YYYY"
            value={filters.year}
            onChange={(e) => updateFilter('year', e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500 outline-none"
          />
        </div>

        {/* Rarity */}
        <div className="space-y-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Rarity
          </label>
          <select
            title="Rarity filter"
            value={filters.rarity}
            onChange={(e) => updateFilter('rarity', e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500 outline-none appearance-none"
          >
            <option value="">Any Rarity</option>
            {RARITIES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Sort */}
        <div className="space-y-1">
          <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <ArrowDownUp className="w-3 h-3" /> Sort Order
          </label>
          <select
            title="Sort order"
            value={filters.sort}
            onChange={(e) => updateFilter('sort', e.target.value as any)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500 outline-none appearance-none"
          >
            <option value="price-desc">Highest Value</option>
            <option value="price-asc">Lowest Value</option>
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-slate-800 pt-3">
        <button
          type="button"
          onClick={() => onChange({ minPrice: '', maxPrice: '', year: '', rarity: '', sort: 'price-desc' })}
          className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-white transition-colors"
        >
          Reset
        </button>
        {mode === 'explore' && (
          <button
            type="button"
            onClick={onApply}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-black uppercase tracking-widest transition-colors shadow-lg shadow-blue-600/20"
          >
            Apply Filters
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
